import { Component, NgZone, OnDestroy, OnInit, inject, effect } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom, fromEvent, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CabinetService } from '../../../domain/cabinet/service/cabinet.service';
import { URL_PRESET } from './service/url-preset';
import { AuthService, UserType } from 'src/app/domain/auth/service/auth.service';
import {
  InitDataUserAccess,
  OrganizationWithAccesses,
} from 'src/app/core/component/hornavbar/dto/InitDataDTO';
import { HorNavBarShowElements } from './dto/HorNavBarShowElements';
import { URLToHeaderName } from './service/URLToHeaderName';
import { CookieService } from 'ngx-cookie-service';
import { InitStateBusinessService } from 'src/app/core/service/init-state-business.service';
import { Location } from '@angular/common';
import { RegExpService } from '../../../util/common/reg-exp.service';
import { OrgPickerComponent } from './org-picker/org-picker.component';
import { OrganizationDTO } from 'src/app/domain/organization/dto/OrganizationDTO';
import { EmployeeRole } from 'src/app/domain/user/employee/dto/role-type';
import { OrganizationsService } from "../../../domain/organization/service/organizations.service";
import { ButtonComponent } from "../../../domain/shared/component/buttons/button.component";
import { ProfileDTO } from "../../../domain/user/profile/dto/ProfileDTO";
import { NavAvatarComponent } from "../../../domain/user/profile/component/avatar/nav-avatar.component";
import { cloneDeep } from "lodash";
import { OrcaLogoComponent } from "../orca-logo-svg/orca-logo-svg.component";
import { ShareModalComponent } from "./share-modal/share-modal.component";
import { ModalContainerComponent } from "../../../domain/shared/component/modal-container/modal-container.component";
import { NewOrganization } from "../../../domain/organization/component/new-organization/new-organization";
import { ModalTeamMemberComponent } from "../../../domain/organization/component/team/modal-team-member.component";
import { EmployeeDTO } from "../../../domain/user/employee/dto/EmployeeDTO";
import { EmployeeService } from "../../../domain/user/employee/service/employee.service";
import { NOTIFICATIONS, NotificationsService } from "../../service/notifications.service";
import { ModalDialogComponent } from "../../../domain/shared/component/modal-dialog/modal-dialog.component";
import { VideoPlayerComponent } from "../../../domain/shared/component/video-player/video-player.component";
import {
  ABOUT_ORCA_BUDGET_MODEL,
  ABOUT_ORCA_INVESTMENT_MODEL,
  ABOUT_ORCA_TEXT,
  ABOUT_ORCA, HOW_TO_USE_THIS_MODEL_TEXT
} from "../../../util/common/links";
import { VideoShowDto } from "../../../domain/user/dto/VideoShowDto";
import { UserBackendService } from "../../../domain/user/service/UserBackendService";
import { FRONT_URLS, MODELS_URLS } from "../../../util/common/url-parts";
import {
  UnsavedChangesModalService,
} from "../../../domain/shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";

@Component({
  selector: 'app-horizontal-nav-bar',
  templateUrl: './horizontal-nav-bar.component.html',
  styleUrls: ['./horizontal-nav-bar.component.scss', '../../../domain/entrance/component/base/entrance.component.scss'],
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    OrgPickerComponent,
    NavAvatarComponent,
    OrcaLogoComponent,
    ShareModalComponent,
    ModalContainerComponent,
    NewOrganization,
    ModalTeamMemberComponent,
    ModalDialogComponent,
    VideoPlayerComponent,
  ],
  standalone: true,
})
export class HorizontalNavBarComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private cookie = inject(CookieService);
  private ngZone = inject(NgZone);
  private location = inject(Location);

  private authService = inject(AuthService);
  private employeeService = inject(EmployeeService);
  private cabinetService = inject(CabinetService);
  private organizationsService = inject(OrganizationsService);
  private notificationService = inject(NotificationsService);
  private userBackendService = inject(UserBackendService);
  private initService = inject(InitStateBusinessService);
  private unsavedChangesService = inject(UnsavedChangesModalService);


  chosenVideoToShow: string = ABOUT_ORCA;
  videoButtonText: string = ABOUT_ORCA_TEXT;
  organizations: OrganizationDTO[] = [];
  organizationsWithRoles: OrganizationWithAccesses[] = [];
  selectedOrganization: OrganizationDTO;
  currentPageHeader = '';
  is = new IsHorNav();

  currentPage: string;
  currentService: SOFTWARE_SERVICE;

  videoShowDto: VideoShowDto;

  /**
   * Which basic elements in the header should be shown. They change depending on the url.
   */
  showElements: HorNavBarShowElements = {
    orgPicker: true,
    adminSwitcher: true,
    playShareOrgButtons: true,
    logo: true,
  };

  isMultiAccess = false;
  userType: UserType = 'viewer';
  hasScrolledDown = false;

  employees: EmployeeDTO[] = [];
  currentEmployee: EmployeeDTO;
  userForDeleteModal: EmployeeDTO;
  deleteMemberText: string;

  constructor() {
    effect(() => {
      const data = this.initService.currentInitData();
      if (data !== undefined) {
        this.cabinetService.setProfile(data.profile);
        this.setUserInfo(data.profile, data.userAccess);
        this.organizationsWithRoles = data.organizations;
        this.organizationsService.setAll(data.organizations);
        this.videoShowDto = data.videoShowDto;
        this.handleVideoShowDto();
        this.is.showCreateOrgBtn = data.isNewOrgCanBeCreated;
      }
    });
  }

  ngOnInit(): void {
    this.initSubscription();
    this.defineCurrentPageAndService();
    this.updateShowElements();
  }

  ngOnDestroy(): void {
    this.organizations = [];
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  showVideoModal(videoId: string): void {
    this.chosenVideoToShow = videoId;
    this.is.showAboutOrcaModal = true;
  }

  getVideoByUrl(): string {
    const url = this.router.url;
    const hasOrgParam = Boolean(this.activatedRoute.snapshot.queryParams.o);

    switch (true) {
      case url.includes(FRONT_URLS.ENTRANCE) && hasOrgParam:
        return ABOUT_ORCA;
      case url.includes(MODELS_URLS.FUND_BUDGET):
        return ABOUT_ORCA_BUDGET_MODEL;
      case url.includes(MODELS_URLS.INVESTMENT):
        return ABOUT_ORCA_INVESTMENT_MODEL;
    }
  }

  closeAboutOrcaModal(): void {
    this.is.showAboutOrcaModal = false;
  }

  async openShareModal(): Promise<void> {
    await this.getEmployees();
    this.is.showShareModal = true;
  }

  closeShareModal(): void {
    this.is.showShareModal = false;
  }

  openInviteMemberModal(): void {
    this.is.showShareModal = false;
    this.is.showInviteMemberModal = true;
  }

  closeInviteMemberModal(): void {
    this.is.showInviteMemberModal = false;
    this.is.showShareModal = true;
  }

  async addMember(employee: EmployeeDTO): Promise<void> {
    try {
      const inviteData = {
        ...employee,
        orgUUID: this.selectedOrganization.uuid,
      };

      const createdEmployee = await firstValueFrom(
        this.employeeService.create(inviteData)
          .pipe(takeUntil(this._destroyStream)),
      );
      this.employees = [...this.employees, createdEmployee];
      this.notificationService.success(NOTIFICATIONS.INVITE_SENT_SUCCESS);
      this.is.showInviteMemberModal = false;
      this.is.showShareModal = true;
    } catch (error) {
      if (error.status === 422) {
        this.notificationService.error(NOTIFICATIONS.DUPLICATE_EMAIL_ERROR);
      }
    }
  }

  openDeleteMemberModal(employee: EmployeeDTO): void {
    this.userForDeleteModal = employee;
    this.deleteMemberText = `I understand that ${employee.mail} will be deleted`;
    this.is.showDeleteEmployeeModal = true;
  }

  async closeModalDeleteUser(isConfirmed: boolean): Promise<void> {
    if (isConfirmed) {
      await firstValueFrom(
        this.employeeService.delete(this.userForDeleteModal.uuid)
          .pipe(takeUntil(this._destroyStream)),
      );
      this.employees = this.employees.filter((e) => e.uuid !== this.userForDeleteModal.uuid);
    }
    this.is.showDeleteEmployeeModal = false;
  }

  async resendInvite(employee: EmployeeDTO): Promise<void> {
    await firstValueFrom(
      this.employeeService.reInviteUser(employee)
        .pipe(takeUntil(this._destroyStream)),
    );
    this.notificationService.success(NOTIFICATIONS.INVITE_SENT_SUCCESS);
  }

  async onUpdateMember(data: { employee: EmployeeDTO; role: EmployeeRole }): Promise<void> {
    const updatedEmployee: EmployeeDTO = {
      ...data.employee,
      role: data.role,
    };
    await firstValueFrom(
      this.employeeService.update(updatedEmployee)
        .pipe(takeUntil(this._destroyStream)),
    );

    const employeeIndex = this.employees.findIndex((emp) => emp.uuid === data.employee.uuid);
    if (employeeIndex !== -1) {
      this.employees[employeeIndex] = { ...this.employees[employeeIndex], role: data.role };
    }
  }

  async clickOnOrganization(): Promise<void> {
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }
    await this.router.navigate([FRONT_URLS.ORG_SETTINGS], { queryParams: this.activatedRoute.snapshot.queryParams });
  }

  async changeSelectedOrganization(orgWithAccesses: OrganizationWithAccesses): Promise<void> {
    // Select the same org?
    if (orgWithAccesses.organization.uuid === this.selectedOrganization?.uuid) {
      return;
    }
    // Has unsaved changes?
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }

    await this.organizationsService.setSelected(orgWithAccesses.organization);
    await this.router.navigate([], {
      queryParams: {
        o: orgWithAccesses.organization.uuid,
      },
    });
  }

  async openCreateOrgModal(): Promise<void> {
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }
    this.is.showCreateOrgModal = true;
  }

  /**
   * Updates which ui elements are shown in HorNavBar such as org/fund picker, period picker and others
   * based on which page the user is on. currentPage holds the url value of the current page allowing us
   * to compare it with the URL_PRESET values which dictate the elements that are shown. We check both for
   * exact match and also variations.
   *
   * For the company tab, since the URL includes a `companyUuid`, it prevents a complete match
   * with the preset, so have an early check for this specific case.
   */
  updateShowElements(): void {
    Object.keys(URL_PRESET).forEach((key) => {
      const isEqual = key.replace('-', '_') === this.currentPage.replace('-', '_');
      const isContaining = this.currentPage.replace('-', '_').includes(key.replace('-', '_'));
      if (isEqual || (!isEqual && isContaining)) {
        this.showElements = URL_PRESET[key] as HorNavBarShowElements;
      }
    });
  }

  changeViewerAdminView(): void {
    const typeAfterChange = this.userType === 'admin' ? 'viewer' : 'admin';
    this.authService.setCurrentUserType(typeAfterChange);
  }

  checkAccessAfterChange(orgWithUserRole: OrganizationWithAccesses): void {
    if (!orgWithUserRole) {
      return;
    }
    switch (this.currentService) {
      case SOFTWARE_SERVICE.FMS:
        this.setIsMultiAccess(orgWithUserRole.userAccesses.fmsAccess);
        break;
      case SOFTWARE_SERVICE.UTIL_PAGE:
        this.setIsMultiAccess(orgWithUserRole.userAccesses.fmsAccess);
        break;
    }
  }

  onlyShowVideo(): void {
    const videoLink = this.getVideoByUrl();
    if (videoLink) {
      this.showVideoModal(videoLink);
    }
  }

  /**
   * Return user to the previous page
   */
  goBack(): void {
    this.location.back();
  }

  private initSubscription(): void {
    this.subscribeToScroll();

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.defineCurrentPageAndService();
        this.updateShowElements();
        this.handleVideoShowDto();
        window.scrollTo(0, 0);
      }
    });

    // All organizations fetch
    this.organizationsService.all$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((organizations) => {
        if (!organizations) {
          return;
        }
        this.organizations = organizations;

        this.selectOrgFromQueryParam();
      });

    // Subscribe on selected organization change.
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrganization = org;
        this.checkAccessAfterChange(this.organizationsWithRoles.find((o) => o.organization.uuid === org.uuid));
      });

    // this.organizationsService.selectedWithAccesses$
    //   .pipe(takeUntil(this._destroyStream))
    //   .subscribe((org) => {
    //     if (!org) {
    //       return;
    //     }
    //     this.orgAccesses = this.organizationsService.selectedOrgAccesses;
    //     this.showUserManagementOptions = (this.orgAccesses.isSuperAdmin || this.orgAccesses.isOrganizationAdmin) ||
    //       org.userAccesses.hasAtLeastOneAccess(org);
    //   });

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.userType = userType;
      });
  }

  private setIsMultiAccess(access: EmployeeRole): void {
    this.isMultiAccess = access.toString().toLowerCase().includes('admin');
    if (!this.isMultiAccess && this.userType === 'admin') {
      this.changeViewerAdminView();
    }
  }

  private defineCurrentPageAndService(): void {
    this.currentPage = this.parseCurrentPageURL(this.router.url);
    this.defineVideoButtonText(this.currentPage);
    this.currentPageHeader = URLToHeaderName.getHeaderName(this.currentPage);
    const urlHeader = this.currentPage.split('/')[0];
    const nextService = URLToHeaderName.getServiceName(urlHeader);
    if (nextService && (!this.currentService || this.currentService !== nextService)) {
      this.currentService = nextService;
      // this portion only makes sense in entrance?
      if (this.selectedOrganization) {
        this.checkAccessAfterChange(
          this.organizationsWithRoles
            .find((o) => o.organization.uuid === this.selectedOrganization.uuid));
      }
    }
  }

  private defineVideoButtonText(currentPage: string):void {
    this.videoButtonText = (currentPage === 'entrance') ? ABOUT_ORCA_TEXT : HOW_TO_USE_THIS_MODEL_TEXT;
  }

  /**
   * ToDo: will be refactored by putting in the outer service. Do not forget to update regexp service (pr awaiting for 2.0)
   * <p>
   * Provides the clean url without parameters and uuids.
   * This is used e.x. by hornavbar to show fp icons depending on the current page.
   * <p>
   * Precondition: Expects to start with a slash after the domain.
   * <pre>
   * Example:
   * Given the   "/portfolios/company/458a479d-cda8-44d8-93da-93d539d0cdb4?f=8cf928b0-db54-4cae-976d-f56c34309817"
   * Will return "portfolios/company"
   * </pre>
   *
   * @param url - can be retrieved by `this.router.url`.
   * @returns the url without parameters and uuids.
   */
  private parseCurrentPageURL(url: string): string {
    const urlWithoutParams = url.substring(1).split('?')[0];
    const lastSlashPart = urlWithoutParams.split('/').at(-1);
    // if the last url part is uuid, remove it. E.x.: portfolio/company/458a479d-cda8-44d8... to portfolio/company
    if (RegExpService.uuidV4.test(lastSlashPart)) {
      return urlWithoutParams.substring(0, urlWithoutParams.lastIndexOf('/'));
    }
    return urlWithoutParams;
  }

  private _filterSingleOrg(value: string): OrganizationDTO[] {
    const filterValue = value.toLowerCase();
    const orgsForFilter = cloneDeep(this.organizations);
    return orgsForFilter.filter((el) =>
      el.name.toLowerCase().includes(filterValue),
    );
  }

  private async selectOrgFromQueryParam(): Promise<void> {
    // parse org to set it after refresh/new page
    const params = this.activatedRoute.snapshot.queryParams;
    const { o: orgUuid } = params as { o?: string; };
    if (!orgUuid) {
      return;
    }
    const selectedOrg = this.organizations.find((o) => o.uuid === orgUuid);
    if (!selectedOrg) {
      return;
    }
    await this.organizationsService.setSelected(selectedOrg);
  }

  private handleVideoShowDto(): void {
    if (!this.videoShowDto) {
      return;
    }
    const url = this.router.url;
    const hasOrgParam = this.activatedRoute.snapshot.queryParams.o;

    if (!this.videoShowDto.isEntranceVideoShowed && url.includes(FRONT_URLS.ENTRANCE) && hasOrgParam) {
      this.showVideoModal(ABOUT_ORCA);
      this.videoShowDto.isEntranceVideoShowed = true;
      this.userBackendService.updateViewSettings(this.videoShowDto).subscribe();
      return;
    }

    if (!this.videoShowDto.isBudgetVideoShowed && (url.includes(MODELS_URLS.FUND_BUDGET) || url.includes(MODELS_URLS.INVESTMENT))) {
      this.showVideoModal(url.includes(MODELS_URLS.FUND_BUDGET) ? ABOUT_ORCA_BUDGET_MODEL : ABOUT_ORCA_INVESTMENT_MODEL);
      this.videoShowDto.isBudgetVideoShowed = true;
      this.userBackendService.updateViewSettings(this.videoShowDto).subscribe();
    }
  }

  private setUserInfo(profile: ProfileDTO, userAccess: InitDataUserAccess): void {
    const authByCookie = this.cookie.get('freemium_user_info');
    const splitAuthCookie = authByCookie.split('&');
    const userId = Number(splitAuthCookie[0]);
    const userMail = splitAuthCookie[1];
    this.authService.setUserInfo({
      id: userId,
      mail: userMail,
      name: profile.name,
      userRole: userAccess.userRole,
      portalRole: userAccess.portalRole,
    });
  }

  private subscribeToScroll(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll')
        .pipe(takeUntil(this._destroyStream), distinctUntilChanged(), debounceTime(100))
        .subscribe(() => this.checkIfScrolled());
    });
  }

  private checkAndSetIsSwitcherDisabled(): void {
    this.is.switcherDisabled = this.userType === 'admin';
  }

  private checkIfScrolled(): void {
    const currentScroll = window.scrollY > SCROLL_THRESHOLD_PX;
    if (currentScroll !== this.hasScrolledDown) {
      this.ngZone.run(() => {
        this.hasScrolledDown = currentScroll;
      });
    }
  }

  private async getEmployees(): Promise<void> {
    this.employees = await firstValueFrom(this.employeeService.get(this.selectedOrganization.uuid));
    this.checkOrgAccess();
  }

  private checkOrgAccess(): void {
    const userInfo = this.authService.userInfoValue;
    this.currentEmployee = this.employees.find((e) => e.mail === userInfo.mail);
  }
}

const SCROLL_THRESHOLD_PX = 10;

export class IsHorNav {
  switcherDisabled = false;
  showCreateOrgBtn = false;
  // Modals
  showCreateOrgModal = false;
  showShareModal = false;
  showInviteMemberModal = false;
  showDeleteEmployeeModal = false;
  showAboutOrcaModal = false;
}

export enum SOFTWARE_SERVICE {
  FMS = 'FMS',
  UTIL_PAGE = 'UTIL_PAGE',
}
